<template>
  <modal
    :name="modalName"
    width="96%"
    height="auto"
    :max-width="920"
    :adaptive="true"
    :scrollable="true"
    @opened="opened"
    @before-close="beforeClose"
  >
    <section
      ref="dialog"
      class="rating-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="first-reply-rating-title"
      tabindex="-1"
    >
      <AppModalHeader
        ref="header"
        design="new"
        title-id="first-reply-rating-title"
        eyebrow="First reply rating"
        :title="detail ? detail.lead.name || `Lead #${detail.lead.id}` : 'First reply rating'"
        @close="$emit('close')"
      />
      <div class="rating-dialog__body" :aria-busy="loading">
        <div v-if="loading && !detail" class="rating-dialog__skeleton" role="status" aria-label="Loading rating">
          <span v-for="index in 5" :key="index" aria-hidden="true" />
        </div>
        <div v-else-if="error" class="rating-dialog__error" role="alert">
          <span>{{ error }}</span>
          <AppButton design="new" theme="dark" @click.native="load">Retry</AppButton>
        </div>
        <template v-else-if="detail">
          <div class="rating-dialog__summary">
            <AppStatusBadge design="new" :tone="status.tone" :label="status.label" />
            <AppStatusBadge
              v-if="detail.score !== null"
              design="new"
              :tone="scoreTone(detail.score)"
              :label="`${Number(detail.score).toFixed(1)} / 10`"
            />
            <span v-if="detail.reason" class="rating-dialog__reason">{{ reasonLabel(detail.reason) }}</span>
          </div>
          <section v-if="detail.issues && detail.issues.length" class="rating-dialog__feedback rating-dialog__feedback--improve">
            <h3>What to improve</h3>
            <ol class="rating-dialog__improvements">
              <li v-for="(issue, index) in detail.issues" :key="index">
                <p>{{ issue }}</p>
                <span>Recommendation</span>
                <p>{{ detail.improvements[index] }}</p>
              </li>
            </ol>
          </section>
          <section v-if="detail.strength" class="rating-dialog__feedback rating-dialog__feedback--strength">
            <h3>Main strength</h3>
            <p>{{ detail.strength }}</p>
          </section>
          <p v-else-if="detail.explanation" class="rating-dialog__explanation">{{ detail.explanation }}</p>
          <dl class="rating-dialog__meta">
            <div>
              <dt>Lead</dt>
              <dd>
                <router-link :to="{ name: 'crm-task-tracker-leads', query: { lead: String(detail.lead.id) } }">
                  {{ detail.lead.name || `Lead #${detail.lead.id}` }}
                </router-link>
              </dd>
            </div>
            <div>
              <dt>Trengo ticket</dt>
              <dd>
                <router-link :to="{ name: 'crm-trengo-matching', query: { ticket: String(detail.ticket.id) } }">
                  {{ detail.ticket.external_id }}
                </router-link>
                <span v-if="detail.ticket.subject"> · {{ detail.ticket.subject }}</span>
              </dd>
            </div>
            <div>
              <dt>Manager</dt>
              <dd>{{ detail.manager ? detail.manager.full_name : '—' }}</dd>
            </div>
            <div>
              <dt>Analysed</dt>
              <dd>{{ dateLabel(detail.analyzed_at) }}</dd>
            </div>
            <div>
              <dt>Run</dt>
              <dd>#{{ detail.run }} · {{ detail.source === 'auto' ? 'Automatic' : 'Manual' }}</dd>
            </div>
            <div>
              <dt>Criteria</dt>
              <dd>{{ detail.criteria_version || '—' }}</dd>
            </div>
          </dl>

          <section v-if="detail.reply" class="rating-dialog__section" aria-label="Rated reply context">
            <template v-if="detail.initial_request">
              <h3>Initial website request</h3>
              <p class="rating-dialog__initial-request">{{ detail.initial_request }}</p>
            </template>
            <template v-if="detail.client_messages && detail.client_messages.length">
              <h3>Customer messages before the reply</h3>
              <ol class="rating-dialog__messages">
                <li v-for="message in detail.client_messages" :key="message.id">
                  <time>{{ dateLabel(message.date) }}</time>
                  <p>{{ message.text }}</p>
                </li>
              </ol>
            </template>
            <h3>Rated manager reply</h3>
            <div class="rating-dialog__reply">
              <time>{{ dateLabel(detail.reply.date) }}</time>
              <p>{{ detail.reply.text }}</p>
            </div>
          </section>
          <p v-else class="rating-dialog__muted">No reply was selected for this ticket.</p>

          <div v-if="canEdit" class="rating-dialog__actions">
            <AppButton
              ref="rerun"
              design="new"
              theme="info"
              :disabled="active || rerunning"
              :aria-busy="rerunning ? 'true' : null"
              @click.native="rerun"
            >
              Rerun analysis
            </AppButton>
          </div>
        </template>
      </div>
    </section>
  </modal>
</template>

<script>
import { getTrengoFirstReplyRating, postTrengoFirstReplyRerun } from '@/http'
import { dateLabel, errorMessage } from '@/views/tech-support/integrations/model'
import { techSupportKey } from '@/utils/techSupportAccess'
import { isActive, reasonLabel, scoreTone, statusOf } from './model'

export default {
  name: 'FirstReplyRatingModal',
  props: { id: { type: [String, Number], required: true } },
  data: () => ({ detail: null, loading: false, rerunning: false, error: '', generation: 0, pollTimer: null }),
  computed: {
    modalName() { return `first-reply-rating-${this.id}` },
    canEdit() { return this.$store.getters['permissions/can'](techSupportKey('trengo', 'edit')) },
    status() { return statusOf(this.detail.status) },
    active() { return Boolean(this.detail) && isActive(this.detail) }
  },
  created() { this.load() },
  mounted() {
    this.$modal.show(this.modalName)
    this.pollTimer = setInterval(() => {
      if (!document.hidden && !this.loading && this.active) this.load(true)
    }, 5000)
  },
  beforeDestroy() {
    this.generation++
    clearInterval(this.pollTimer)
  },
  methods: {
    dateLabel,
    reasonLabel,
    scoreTone,
    opened() { this.$refs.header.focusClose() },
    beforeClose(event) {
      event.cancel()
      this.$emit('close')
    },
    async load(silent = false) {
      const generation = ++this.generation
      if (!silent) this.loading = true
      this.error = ''
      try {
        const response = await getTrengoFirstReplyRating(this.id)
        if (generation !== this.generation) return
        const changed = this.detail && this.detail.status !== response.data.status
        this.detail = response.data
        if (changed) this.$emit('changed')
      } catch (error) {
        if (generation === this.generation) this.error = errorMessage(error, 'Rating')
      } finally {
        if (generation === this.generation) this.loading = false
      }
    },
    async rerun() {
      if (!this.canEdit || this.rerunning) return
      this.rerunning = true
      this.$refs.rerun.preload = true
      try {
        await postTrengoFirstReplyRerun(this.id)
        this.$notify({ type: 'success', title: 'Analysis queued' })
        this.$emit('changed')
        await this.load(true)
      } catch (error) {
        this.$notify({ type: 'error', title: errorMessage(error, 'Rating') })
      } finally {
        this.rerunning = false
        if (this.$refs.rerun) this.$refs.rerun.preload = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.rating-dialog {
  background: $ui-surface;
  border-radius: $ui-overlay-radius;
  color: $ui-text;
  font-family: $ui-font-body;
  font-size: $ui-font-size-body;

  &__body {
    display: flex;
    flex-direction: column;
    gap: $ui-space-16;
    padding: $ui-space-20 $ui-space-24 $ui-space-24;
  }

  &__skeleton {
    display: grid;
    gap: $ui-space-16;

    span {
      height: 32px;
      border-radius: $ui-control-radius;
      background: $ui-line;
    }
  }

  &__error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $ui-space-16;
    padding: $ui-space-16;
    border-radius: $ui-panel-radius;
    background: $ui-red-soft;
    color: $ui-red;
  }

  &__summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $ui-space-8;
  }

  &__reason,
  &__muted {
    color: $ui-muted;
    font-size: $ui-font-size-metadata;
    line-height: 1.5;
  }

  &__explanation {
    margin: 0;
    padding: $ui-space-16;
    border-radius: $ui-panel-radius;
    background: $ui-surface-soft;
    color: $ui-ink;
    line-height: 1.5;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  &__feedback {
    padding: $ui-space-16;
    border-radius: $ui-panel-radius;
    background: $ui-surface-soft;

    h3 {
      margin: 0 0 $ui-space-8;
      color: $ui-ink;
      font-family: $ui-font-heading;
      font-size: $ui-font-size-section-title;
      font-weight: 700;
    }

    p {
      margin: 0;
      line-height: 1.5;
      overflow-wrap: anywhere;
    }
  }

  &__feedback--improve {
    background: $ui-amber-soft;

    .rating-dialog__improvements li {
      border-color: #fbd7ad;
    }
  }

  &__improvements {
    display: grid;
    gap: $ui-space-12;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      padding: $ui-space-12 $ui-space-16;
      border: 1px solid $ui-line;
      border-radius: $ui-panel-radius;
      background: $ui-surface;
    }

    span {
      display: block;
      margin-top: $ui-space-8;
      color: $ui-muted;
      font-size: $ui-font-size-metadata;
      font-weight: 600;
    }
  }

  &__meta {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: $ui-space-12;
    margin: 0;

    > div {
      padding: $ui-space-12 $ui-space-16;
      border: 1px solid $ui-line;
      border-radius: $ui-panel-radius;
      background: $ui-surface-soft;
    }

    dt {
      margin-bottom: $ui-space-4;
      color: $ui-muted;
      font-size: $ui-font-size-metadata;
    }

    dd {
      margin: 0;
      overflow-wrap: anywhere;
    }

    a {
      color: $ui-blue;
      font-weight: 600;
    }
  }

  &__section {
    padding-top: $ui-space-16;
    border-top: 1px solid $ui-line;

    h3 {
      margin: 0 0 $ui-space-12;
      color: $ui-ink;
      font-family: $ui-font-heading;
      font-size: $ui-font-size-section-title;
      font-weight: 700;
    }
  }

  &__messages {
    display: grid;
    gap: $ui-space-12;
    margin: 0 0 $ui-space-24;
    padding: 0;
    list-style: none;
  }

  &__initial-request {
    margin: 0 0 $ui-space-24;
    padding: $ui-space-12 $ui-space-16;
    border: 1px solid $ui-line;
    border-radius: $ui-panel-radius;
    background: $ui-surface;
    line-height: 1.5;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  &__messages li,
  &__reply {
    padding: $ui-space-12 $ui-space-16;
    border: 1px solid $ui-line;
    border-radius: $ui-panel-radius;
    background: $ui-surface;
  }

  &__reply {
    border-color: $ui-blue;
    background: $ui-blue-soft;
  }

  &__messages time,
  &__reply time {
    display: block;
    margin-bottom: $ui-space-4;
    color: $ui-muted;
    font-size: $ui-font-size-metadata;
  }

  &__messages p,
  &__reply p {
    margin: 0;
    line-height: 1.5;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: $ui-space-8;
  }
}

@media (max-width: 720px) {
  .rating-dialog__meta {
    grid-template-columns: 1fr;
  }
}
</style>
